import { Company } from '@apis/Customers/model';
import { postExportExport } from '@apis/Export';
import { ExportRequest, ExportRequestTarget, ExportRequestType } from '@apis/Export/model';
import { Query } from '@apis/Invoices/model';
import { QueryExpr, QueryResult } from '@apis/Resources';
import { Group, ThemeIcon, Text } from '@mantine/core';
import { FillerSwitch } from '@root/Design/Filler';
import { ICompanyContextToken } from '@root/Services/Customers/CompanyContext';
import { useDi, useDiContainer } from '@root/Services/DI';
import { EventEmitter, useEventValue } from '@root/Services/EventEmitter';
import { FormatService } from '@root/Services/FormatService';
import { IInvoiceFieldCompatibilityLookup, InvoiceFieldCompatibilityService } from '@root/Services/Invoices/InvoiceFieldCompatibilityService';
import { IBaseInvoiceRecord, IDailyRollup, InvoiceSchemaService, ITranformationDetail } from '@root/Services/Invoices/InvoiceSchemaService';
import { NotificationService } from '@root/Services/Notification/NotificationService';
import { FieldInfo, groupExprs, naiveJsonCopy, SchemaService, SchemaValueProvider, ValuesGroupOtherText } from '@root/Services/QueryExpr';
import { ComponentProps, memo, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { FileSpreadsheet } from 'tabler-icons-react';
import { inject, injectable } from 'tsyringe';
import { ActivityPanelModel } from '../Actions/ActivityPanel/Models';
import { DataGrid } from '../DataGrid';
import { DataGridModel } from '../DataGrid/DataGridModel';
import { FieldInfoColumnAdapter } from '../DataGrid/FieldInfoColumnAdapter';
import { ColumnConfig, DataGridProps, DataGridState, GridGroupByState, IDataSource, ISelectionStrategy } from '../DataGrid/Models';
import { QueryExprGroupHelper } from '../DataGrid/QueryExprGroupHelper';
import { IValueProviderFactory } from '@root/Services/Query/QueryDatasource';
import { IDashboardConfig } from '../DashboardLayout/Models';

interface InvoiceApi {
    query: <T>(query: Query, dateRange: { from?: Date; to?: Date }, appendRangeCheck?: boolean) => Promise<QueryResult<T>>;
    cleanQueryFields: (query: Query, transformations: ITranformationDetail) => Query;
}

export function DailyInvoiceGrid({
    persistenceKey,
    invoiceApi,
    onFilterChange,
    scope,
    dateRange,
    defaultGroupBy,
    rightPlaceholder,
    filtersDisabled,
    onSelectionChanged,
    selectionStrategy,
    renderRowSelector,
    disabledSavedViews,
    maxLineItems,
    costField,
    filters,
    view,
    onGridPropsReady,
}: {
    persistenceKey: string;
    invoiceApi: InvoiceApi;
    onFilterChange?: (filter: QueryExpr[]) => void;
    defaultGroupBy: GridGroupByState[];
    rightPlaceholder?: ReactNode;
    filtersDisabled?: boolean;
    onSelectionChanged?: (selectionState: { getItems: () => Promise<DailyInvoiceRow[]> }) => void;
    selectionStrategy?: ISelectionStrategy<DailyInvoiceRow>;
    renderRowSelector?: (
        item: DailyInvoiceRow | null,
        selectionState: { selected?: boolean; some?: boolean; all?: boolean; toggle: (selected: boolean) => void },
        isHeader: boolean
    ) => ReactNode;
    disabledSavedViews?: boolean;
    maxLineItems?: number;
    costField?: string;
    /**
     * Scope applies a filter to all queries made by the grid
     */
    scope?: QueryExpr;
    /**
     * Date range determines which indexed months are queried, but does not filter by date
     */
    dateRange: { from?: Date; to?: Date };
    /**
     * Filters, like scope, apply filters to all queries made by the grid
     */
    filters?: QueryExpr[];
    /**
     * The name of the saved view to load
     */
    view?: string;
    /**
     * onGridPropsReady is called just before the grid props are applied.
     * This can be used to modify all grid props allowing full customization.
     */
    onGridPropsReady?: (props: DataGridProps<DailyInvoiceRow>) => DataGridProps<DailyInvoiceRow>;
}) {
    const container = useDiContainer();

    const model = useMemo(() => container.resolve(DailyInvoicesGridModel).init(invoiceApi, defaultGroupBy, costField), []);

    useEffect(() => model.setFilters(filters), [model, JSON.stringify(filters)]);
    useEffect(() => model.setScope(scope), [model, JSON.stringify(scope)]);
    useEffect(() => model.handleCostFieldChanged(costField), [model, costField]);
    useEffect(() => model.setDateRange(dateRange?.from, dateRange?.to), [model, dateRange.from, dateRange.to]);
    useEffect(() => model.setOnFilterChanged(onFilterChange), [model, onFilterChange]);
    useEffect(() => model.setMaxLineItems(maxLineItems), [model, maxLineItems]);
    useEffect(() => model.setSelectionEnabled(!!selectionStrategy), [model, selectionStrategy]);

    const selectionProps = onSelectionChanged ? { selectionMode: 'multiple' as 'multiple', onSelectedChanged: onSelectionChanged } : {};

    const initializing = useEventValue(model.initializing);

    const statsRenderer = useCallback(() => <InvGridStats model={model} />, [model]);

    const statePersistence = useMemo(
        (): ComponentProps<typeof DataGrid>['statePersistence'] =>
            persistenceKey
                ? {
                      key: persistenceKey,
                      defaultView: (layouts) => {
                          const typedLayouts = layouts as unknown as { layout: IDashboardConfig & { systemName?: string } }[];
                          return typedLayouts.find((l) => l.layout.systemName === view) as { layout: IDashboardConfig; id: number };
                      },
                  }
                : undefined,
        [persistenceKey, view]
    );

    return (
        <FillerSwitch loading={initializing}>
            {() => {
                const defaultGridProps = {
                    statePersistence,
                    columns: model.columns,
                    dataSource: model.datasource!,
                    filterValueProvider: model.filterValueProvider!,
                    onModelLoaded: model.attach,
                    onRowClick: model.onGridRowClick,
                    schemaSvc: model.schema,
                    state: model.state!,
                    childAccessor: model.childAccessor,
                    export: model.export,
                    allowNonColumnFilters: true,
                    indentLeafNodes: true,
                    hideGlobalSearch: true,
                    allowGroupBy: true,
                    allowMultiSort: true,
                    groupByAsRows: true,
                    showCount: false,
                    renderStats: statsRenderer,
                    groupByRequired: 1,
                    groupByDisableSort: true,
                    allowSavedViews: disabledSavedViews ? false : true,
                    rightToolPlaceHolder: rightPlaceholder,
                    hideFilterOnly: filtersDisabled,
                    selectionStrategy,
                    ...selectionProps,
                    renderRowSelector: renderRowSelector,
                    minimumLoadingMs: 0,
                };
                const gridProps = onGridPropsReady ? onGridPropsReady(defaultGridProps) : defaultGridProps;
                return <DataGrid {...gridProps} />;
            }}
        </FillerSwitch>
    );
}

export type DailyInvoiceRow = {
    parent?: DailyInvoiceRow;
    value?: string;
    usage?: number;
    rate?: number;
    cost?: number;
    depth: number;
    field?: FieldInfo;
    nullValue: boolean;
} & IDailyRollup;

@injectable()
export class DailyInvoicesGridModel {
    private invoiceApi!: InvoiceApi;
    private gridModel?: DataGridModel;
    private dateRange?: { from?: Date; to?: Date } = {};
    private maxLineItems?: number;
    private scope?: QueryExpr;
    private groupQueryHelper?: QueryExprGroupHelper<DailyInvoiceRow>;
    private defaultGroupBy: GridGroupByState[] = [];
    private onFilterChanged?: (filter: QueryExpr[]) => void;
    private fieldCompatibility?: IInvoiceFieldCompatibilityLookup;
    private refreshThrottle = { key: '', timeout: 0 };
    private costField?: string;

    public initializing = new EventEmitter(true);
    public selectionEnabled = false;
    public datasource?: IDataSource<DailyInvoiceRow>;
    public columns: ColumnConfig<DailyInvoiceRow>[] = [];
    public defaultState?: DataGridState;
    public state?: DataGridState;
    public schema?: SchemaService;
    public filterValueProvider?: IValueProviderFactory;
    public filters?: QueryExpr[];
    public statsChanged = new EventEmitter<{ groupName: string; groups: number; lineItems: number } | undefined>(undefined);

    public constructor(
        @inject(FormatService) private readonly formatSvc: FormatService,
        @inject(InvoiceSchemaService) private readonly invoiceSchemaSvc: InvoiceSchemaService,
        @inject(NotificationService) private readonly notificationSvc: NotificationService,
        @inject(FieldInfoColumnAdapter) private readonly fieldInfoColumnAdapter: FieldInfoColumnAdapter<DailyInvoiceRow>,
        @inject(ICompanyContextToken) private readonly company: Company,
        @inject(ActivityPanelModel) private readonly activityPanelModel: ActivityPanelModel,
        @inject(InvoiceFieldCompatibilityService) private readonly fieldCompatibilityFactory: InvoiceFieldCompatibilityService
    ) {}

    public init(invoiceApi: InvoiceApi, defaultGroupBy: GridGroupByState[], costField?: string) {
        this.invoiceApi = invoiceApi;
        this.defaultGroupBy = defaultGroupBy;
        this.costField = costField;
        this.initialize();
        return this;
    }

    public setDateRange(from?: Date, to?: Date) {
        if (this.dateRange?.from !== from || this.dateRange?.to !== to) {
            this.dateRange = { from, to };
            this.reload();
        }
    }

    public setMaxLineItems(maxLineItems: number | undefined) {
        if (this.maxLineItems !== maxLineItems) {
            this.maxLineItems = maxLineItems;
            this.reload();
        }
    }

    public setSelectionEnabled(selectionEnabled: boolean) {
        this.selectionEnabled = selectionEnabled;
    }

    public setFilters(filters: QueryExpr[] | undefined) {
        if (JSON.stringify(filters) !== JSON.stringify(this.filters)) {
            this.filters = filters;
            this.reload();
        }
    }

    public handleCostFieldChanged(costField?: string) {
        if (this.costField !== costField && costField) {
            this.costField = costField;
            const fieldId = this.schema?.getField(costField)?.pathWithRoot;
            this.gridModel?.addColumnById(`Sum(${fieldId})`);
        }
    }

    public setOnFilterChanged(onFilterChanged?: (filter: QueryExpr[]) => void) {
        if (this.onFilterChanged !== onFilterChanged) {
            this.onFilterChanged = onFilterChanged;
        }
    }

    public setScope(filter?: QueryExpr) {
        if (JSON.stringify(filter) !== JSON.stringify(this.scope)) {
            this.scope = filter;
            this.reload();
        }
    }

    public reload() {
        this.refreshThrottled();
    }

    public setDefaultGroupBy(defaultGroupBy: GridGroupByState[]) {
        this.defaultGroupBy = defaultGroupBy;
        return this;
    }

    public async initialize() {
        try {
            this.initializing.emit(true);
            const types = await this.invoiceSchemaSvc.getDailySchema();
            this.schema = new SchemaService(types);
            this.filterValueProvider = new SchemaValueProvider(this.schema, (q) => {
                q.Where = groupExprs('and', [q.Where, this.createExtFilterCriteria()]);
                return this.runQuery(q);
            });
            this.schema.resolveChildren();
            this.fieldCompatibility = await this.fieldCompatibilityFactory.getLookup(this.schema);
            this.groupQueryHelper = this.createGroupQueryHelper();
            this.datasource = this.createDatasource();
            this.columns = this.createColumns();
            this.defaultState = this.createDefaultState();
            this.state = this.createState();
        } finally {
            this.initializing.emit(false);
        }
    }

    public attach = (gridModel: DataGridModel) => {
        this.gridModel = gridModel;
        this.groupQueryHelper?.attach(gridModel);
        this.gridModel.beforeApplyColumns = (columns) => {
            const valueColumn = gridModel.gridState?.columns.find((c) => c.id === 'value');
            if (valueColumn && !columns.some((c) => c.id === 'value')) {
                columns.unshift(valueColumn);
            }
        };
        this.gridModel.modifyColumnSelectorOptions = (options) => {
            options.selections.splice(0, Infinity, ...options.selections.filter((s) => s.column?.id !== 'value'));
        };
        this.gridModel.gridStateChanged.listen((change) => {
            if (change?.changes.has('filters')) {
                this.onFilterChanged?.(change.state.filters as QueryExpr[]);
            }
        });
        if (this.costField === 'AdjustedCashCost' || this.costField === 'AdjustedAmortizedCost') {
            this.gridModel?.addColumnById(`Sum(Common.${this.costField})`);
        }
    };

    public export = async () => {
        const openActivity = this.activityPanelModel.open;
        try {
            const request = await this.createExportRequest();
            if (request) {
                await postExportExport(request);
                this.notificationSvc.notify(
                    'Export Invoice Details',
                    `Export Requested. Click to check the activity log for download.`,
                    'primary',
                    <ThemeIcon variant="light" size="xl" radius="xl">
                        <FileSpreadsheet />
                    </ThemeIcon>,
                    openActivity
                );
            }
        } catch {
            this.notificationSvc.notify(
                'Export Failed',
                `Error: Export failed. Click to check the activity log for details.`,
                'error',
                null,
                openActivity
            );
        }
    };

    private async createExportRequest(): Promise<ExportRequest | undefined> {
        const name = this.gridModel?.configName ?? 'Invoice Details';
        const fieldIds = [
            ...(this.gridModel?.columns.map((c) => c.config.id) ?? []),
            ...(this.gridModel?.gridState?.groupBy?.map((g) => g.id ?? '') ?? []),
        ];
        const extFilters = this.createExtFilterCriteria();
        const stateOverride = naiveJsonCopy(this.gridModel?.gridState)!;

        const filters = [...(extFilters ? [extFilters] : []), ...stateOverride?.filters];

        this.fieldCompatibility?.adjustQueryFields(filters);

        const transformations = await this.invoiceSchemaSvc.getDailySchemaTransformationDetail();
        const { Where } = this.invoiceApi.cleanQueryFields({ Where: groupExprs('and', stateOverride.filters) }, transformations);
        if (Where) {
            stateOverride.filters = [Where];
        }

        const config = this.gridModel?.getDashboardConfigForExport(name, 'Invoice Details', stateOverride);
        if (config) {
            return {
                CompanyId: this.company.Id,
                Target: ExportRequestTarget.DailyInvoiceQuery,
                Type: ExportRequestType.Excel,
                DashboardConfig: config,
                TargetParameters: {
                    From: this.dateRange?.from ? this.formatSvc.toJsonShortDate(this.dateRange.from) : undefined,
                    To: this.dateRange?.to ? this.formatSvc.toJsonShortDate(this.dateRange.to) : undefined,
                    FieldInfo: this.fieldCompatibility?.getDetailsById(...fieldIds),
                },
            };
        }
    }

    private refreshThrottled() {
        if (this.gridModel) {
            const key = this.createRefreshKey();
            if (key !== this.refreshThrottle.key) {
                clearTimeout(this.refreshThrottle.timeout);
                this.refreshThrottle = {
                    key,
                    timeout: setTimeout(() => {
                        this.gridModel?.refresh();
                    }, 200) as unknown as number,
                };
            }
        }
    }

    private createRefreshKey() {
        return JSON.stringify([this.dateRange, this.scope, this.filters, this.costField]);
    }

    public onGridRowClick = (row: DailyInvoiceRow) => {
        if (this.selectionEnabled) {
            this.gridModel?.setSelected(row, !this.gridModel?.isSelected(row));
        } else {
            this.gridModel?.treeModel?.toggle(row);
        }
    };

    public childAccessor = {
        hasChildren: (row: DailyInvoiceRow) => row.depth < (this.gridModel!.gridState.groupBy?.length ?? 1),
    };

    private createDefaultState() {
        const [date, region, resourceId, qty, rate, cost] = this.getAvailFields([
            'ChargePeriodStart',
            'RegionId',
            'ResourceId',
            'ConsumedQuantity',
            'ContractedUnitPrice',
            'BilledCost',
        ]);
        const costColId = cost ? `Sum(${cost.pathWithRoot})` : 'Sum(lineItem.lineItem/UnblendedCost)';
        return {
            columns: [
                { id: 'value', width: 300, fixed: true },
                { id: date?.pathWithRoot ?? 'Common.UsageStartDate', width: 130 },
                { id: region?.pathWithRoot ?? 'product.product/region', width: 130 },
                { id: resourceId?.pathWithRoot ?? 'lineItem.lineItem/ResourceId', width: 300 },
                { id: qty?.pathWithRoot ?? 'lineItem.lineItem/UsageAmount', width: 130 },
                { id: rate?.pathWithRoot ?? 'lineItem.lineItem/UnblendedRate', width: 160 },
                { id: costColId, width: 160 },
            ],
            filters: this.filters ? this.filters : [],
            sort: cost ? [{ Direction: 'Desc', Expr: { Field: cost.path } }] : [],
            groupBy: this.defaultGroupBy,
        } as DataGridState;
    }

    public createState() {
        const defaultState = this.createDefaultState() as DataGridState;
        if (this.costField === 'AdjustedCashCost' || this.costField === 'AdjustedAmortizedCost') {
            const [costFieldInfo] = this.getAvailFields([this.costField]);
            const costFieldId = costFieldInfo?.pathWithRoot ?? `Common.${this.costField}`;
            defaultState.columns.push({ id: `Sum(${costFieldId})`, width: 160 });
        }
        return defaultState;
    }

    private getAvailFields(fields: string[]) {
        return fields.map((f) => this.fieldCompatibility?.getAvailableField<IBaseInvoiceRecord & Record<string, string>>(f));
    }

    private createColumns() {
        const result: ColumnConfig<DailyInvoiceRow>[] = [];
        for (const field of this.schema!.iterateFields()) {
            if (field.isPrimitive) {
                const column = this.fieldInfoColumnAdapter.adapt(field);
                const accessor = (item: DailyInvoiceRow) => item[field.fieldName as keyof DailyInvoiceRow];
                const { formatter } = column;
                column.accessor = accessor;
                column.cellRenderer = (item) => <>{formatter?.(item, accessor(item))}</>;
                if (column.type === 'string' || column.type === 'date') {
                    column.allowGrouping = true;
                }
                if (column.type === 'number') {
                    column.aggregations = ['Sum', 'Average', 'Min', 'Max'];
                    column.aggAccessor = (item, agg) =>
                        'field' in item ? item[`${agg}(${column.id})` as keyof DailyInvoiceRow] : item[field.fieldName as keyof DailyInvoiceRow];
                }
                result.push(column);
            }
        }
        const ValueCell = createDescriptionCell(this.formatSvc);
        result.push({
            accessor: 'value',
            defaultWidth: 300,
            id: 'value',
            defaultFixed: true,
            header: 'Description',
            noRemove: true,
            noReorder: true,
            type: 'string',
            cellRenderer: (item) => <ValueCell row={item} />,
        });
        return result;
    }

    private createDatasource() {
        return {
            getPage: async (start, end, state, parent) => {
                return this.groupQueryHelper?.getPage(start, end, state, parent, this.createExtFilterCriteria());
            },
        } as IDataSource<DailyInvoiceRow>;
    }

    private createGroupQueryHelper() {
        const [field] = this.getAvailFields(['ChargePeriodStart']);
        return new QueryExprGroupHelper<DailyInvoiceRow>(
            (q) => this.runFlatQuery(q),
            (q, depth) => this.runGroupQuery(q, depth),
            undefined,
            undefined,
            [{ Direction: 'Asc', Expr: { Field: field?.path ?? '' } }],
            { count: null, value: 'value' },
            (col, def) => (col.aggregator ? col.config.id : def)
        );
    }

    private async runGroupQuery(query: Query, depth: number) {
        if (query.Select && query.Select.length) {
            query = { ...query };
        }
        const result = await this.runQuery<DailyInvoiceRow>(query);
        const fieldId = this.gridModel?.gridState?.groupBy?.[depth]?.id;
        const field = this.schema?.getFieldWithId(fieldId ?? '');
        result.Results?.forEach((r) => {
            r.depth = depth;
            r.field = field;
            r.nullValue = r.value === ValuesGroupOtherText;
        });
        if (depth === 0) {
            this.statsChanged.emit({ groupName: field?.name ?? '', groups: result.Results?.length ?? 0, lineItems: result.Count ?? 0 });
        }
        return result;
    }

    private runFlatQuery(query: Query) {
        query.Sort ??= [];
        query.Sort.push({ Direction: 'Asc', Expr: { Field: 'UniqueId' } });
        if (this.maxLineItems) {
            query.Take = this.maxLineItems;
        }
        return this.runQuery<DailyInvoiceRow>(query);
    }

    private createExtFilterCriteria() {
        const result: QueryExpr[] = [];
        if (this.scope) {
            result.push(this.scope);
        }
        if (this.filters?.length) {
            result.push(...this.filters);
        }

        return groupExprs('and', result) as QueryExpr | undefined;
    }

    private async runQuery<T>(query: Query) {
        return this.invoiceApi.query<T>(query, { from: this.dateRange?.from, to: this.dateRange?.to }, false);
    }
}

const InvGridStats = memo(function InvGridStats({ model }: { model: DailyInvoicesGridModel }) {
    const stats = useEventValue(model.statsChanged);
    const fmtSvc = useDi(FormatService);

    if (!stats) {
        return <></>;
    } else {
        const groupCt = stats?.groups ?? 0;
        const groupCtLbl = fmtSvc.formatInt(groupCt);
        const groupsLbl = `${groupCtLbl} ${stats?.groupName} ${groupCt !== 1 ? 'groups' : 'group'}`;

        const lineItemCt = stats?.lineItems ?? 0;
        const lineItemCtLbl = fmtSvc.formatInt(lineItemCt);
        const lineItemsLbl = `${lineItemCtLbl}  ${lineItemCt !== 1 ? 'line items' : 'line item'}`;

        const fullLbl = `${groupsLbl} | ${lineItemsLbl}`;

        return <Group>{!stats ? <></> : <Text color="dimmed">{fullLbl}</Text>}</Group>;
    }
});

function createDescriptionCell(fmtSvc: FormatService) {
    return function DescriptionCell({ row }: { row: DailyInvoiceRow }) {
        const value =
            row.value === ValuesGroupOtherText
                ? 'Other'
                : !row.field
                ? row.value
                : row.field.typeName === 'date'
                ? fmtSvc.toShortDate(fmtSvc.parseDateNoTime(row.value ?? ''))
                : row.value;
        return <>{value ?? ''}</>;
    };
}
